/**
 * True when an error means "we couldn't reach / the server failed", as opposed
 * to an application-level error (a 4xx, a validation failure, a thrown Error).
 * Used by the global error screen to switch to friendlier "service unavailable"
 * copy. Only affects the message shown — never gates behaviour.
 *
 * Two signals:
 *  - a browser `fetch` failure (server down, offline, DNS/connection refused),
 *    which surfaces as a `TypeError` with a "failed to fetch" / "load failed"
 *    (Safari) / "network request failed" message; and
 *  - any error carrying an HTTP-ish status ≥ 500 (server-side fault), including
 *    one nested on `.cause` (how some clients wrap transport errors).
 */
export function isServerUnreachable(error: unknown): boolean {
  if (error == null) return false;

  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    if (/failed to fetch|load failed|networkerror|network request failed/.test(msg)) {
      return true;
    }
  }

  const status = extractStatus(error);
  return status !== null && status >= 500;
}

/** Pull a numeric HTTP status off an error object (or its `.cause`), if any. */
function extractStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null;
  const rec = error as Record<string, unknown>;

  const direct = rec.status ?? rec.statusCode;
  if (typeof direct === "number") return direct;

  if (typeof rec.cause === "object" && rec.cause !== null) {
    const causeStatus = (rec.cause as Record<string, unknown>).status;
    if (typeof causeStatus === "number") return causeStatus;
  }

  return null;
}
